/**
 * An opaque per-tab id used to join a search to the result someone opened.
 *
 * sessionStorage, not localStorage or a cookie: it dies with the tab, is not
 * sent to any other origin, and cannot be joined to an account. It exists to
 * answer "did this search lead to a click", nothing more.
 *
 * Storage arrives through `resolveStorage` rather than touching
 * window.sessionStorage inline, because jest here runs testEnvironment 'node'
 * with jest-environment-jsdom NOT installed — there is no window to reach for
 * in a test, and adding a DOM environment for one file is not worth the
 * dependency.
 */
const KEY = 'tp-search-sid';

interface SessionStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

let injected: SessionStore | null | undefined;
let memoryId: string | null = null;

/** Test seam. Pass null to simulate SSR / no storage. */
export function __setSessionStorageForTests(store: SessionStore | null): void {
  injected = store;
  memoryId = null;
}

function resolveStorage(): SessionStore | null {
  if (injected !== undefined) return injected;
  if (typeof window === 'undefined') return null;
  return window.sessionStorage;
}

const mint = () =>
  Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);

export function getSearchSessionId(): string {
  const store = resolveStorage();
  if (!store) return '';
  try {
    const existing = store.getItem(KEY);
    if (existing) return existing;
    const fresh = mint();
    store.setItem(KEY, fresh);
    return fresh;
  } catch {
    // Private mode and blocked third-party storage both throw. An in-memory
    // id still joins a search to its click within the page's lifetime, which
    // is all this is for — better than dropping capture entirely.
    if (!memoryId) memoryId = mint();
    return memoryId;
  }
}
